import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { auth, db } from "../firebaseConfig";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { doc } from "firebase/firestore";
import { safeGetDoc } from "../lib/safeGet";
import { useNavigate } from "react-router-dom";
import { fetchRoleTabs } from "../hooks/useRoleTabs";
import { primaryRole } from "../lib/roles";
import fundoImage from "../assets/fundo.webp";
import Hero from "../components/Hero";
import Seo from "../components/Seo";
import PageLayout from "../components/PageLayout";
import Footer from "../components/Footer";
import FormContainer from "../components/FormContainer";

const Login = () => {
  const { t } = useTranslation();
  React.useEffect(() => {
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "";
    };
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const [erro, setErro] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // Limpar sessão anterior para garantir estado limpo no AuthContext
      if (auth.currentUser) {
        await signOut(auth);
      }
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      const userDoc = await safeGetDoc(doc(db, "users", user.uid), "profile");

      if (userDoc.exists()) {
        // primaryRole normaliza string|array e escolhe o papel de maior
        // prioridade (master > employee > ... > b2c > affiliates), cobrindo
        // contas multi-papel (ex.: cliente que também virou colaborador).
        const userType = primaryRole(userDoc.data().userType) || "";

        // Redireciona para a rota específica de cada tipo de usuário
        switch (userType) {
          case "master":
            navigate("/company/master/dashboard");
            break;
          case "b2b":
          case "b2c":
          case "colab":
            navigate("/client/projects");
            break;
          case "employee": {
            // O destino é a PRIMEIRA aba liberada para o cargo (employee_types
            // .tabs). Sem regra por nome de cargo — qualquer cargo criado pelo
            // Master cai na sua primeira aba configurada.
            const role = userDoc.data().employeeRole;
            const roleTabs = await fetchRoleTabs(role, userDoc.data().perms);
            navigate(roleTabs.length > 0 ? roleTabs[0].route : "/employee/files");
            break;
          }
          case "manager":
            navigate("/company/manager/dashboard");
            break;
          case "formatter":
            navigate("/company/formatter/dashboard");
            break;
          case "linguist":
            navigate("/company/linguist/dashboard");
            break;
          case "reviewer":
            navigate("/company/reviewer/dashboard");
            break;
          case "translator":
            navigate("/company/translator/dashboard");
            break;
          case "affiliates": {
            // Login unificado: afiliado puro também entra pela tela principal.
            // Mesma checagem de aprovação que a tela /affiliates/login.
            const affSnap = await safeGetDoc(doc(db, "affiliates", user.uid), "affiliate");
            if (!affSnap.exists() || affSnap.data().approved !== true) {
              alert(t("affiliates.awaitingApproval"));
              await signOut(auth);
              return;
            }
            navigate("/affiliates/manage");
            break;
          }
          default:
            alert(t("auth.invalidUserType"));
        }
      } else {
        alert(t("auth.userDocNotFound"));
      }
    } catch (error) {
      console.error("Erro de login:", error);
      
      // Tratamento de erros específicos
      let errorMessage = t("auth.loginError");
      
      if (error.code) {
        switch (error.code) {
          case "auth/invalid-credential":
          case "auth/wrong-password":
          case "auth/user-not-found":
            errorMessage = t("auth.invalidCredentials");
            break;
          case "auth/invalid-email":
            errorMessage = t("auth.invalidEmail");
            break;
          case "auth/user-disabled":
            errorMessage = t("auth.userDisabled");
            break;
          case "auth/too-many-requests":
            errorMessage = t("auth.tooManyRequests");
            break;
          case "auth/network-request-failed":
            errorMessage = t("auth.networkError");
            break;
          default:
            // Para erros 403 ou outros erros de API
            if (error.code.includes("403") || error.message?.includes("403")) {
              errorMessage = "Erro de autenticação (403). Verifique as configurações do Firebase ou entre em contato com o suporte.";
            } else {
              errorMessage = error.message || t("auth.loginError");
            }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErro(errorMessage);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await sendPasswordResetEmail(auth, resetEmail, {
        url: `${window.location.origin}/login?from=reset`,
      });
      setResetMessage(t("auth.resetEmailSent"));
      setResetEmail("");
      setShowForgotPassword(false);
      setTimeout(() => {
        setResetMessage("");
      }, 3000);
    } catch (error) {
      console.error("Erro ao enviar email de recuperação:", error.code, error.message);
      let msg = t("auth.resetEmailError");
      if (error.code === "auth/invalid-email") msg = t("auth.invalidEmail");
      else if (error.code === "auth/user-not-found") msg = t("auth.invalidCredentials");
      else if (error.code === "auth/too-many-requests") msg = t("auth.tooManyRequests");
      else if (error.code === "auth/unauthorized-continue-uri" || error.code === "auth/invalid-continue-uri") {
        msg = "Domínio não autorizado no Firebase. Adicione em Authentication → Settings → Authorized domains.";
      }
      setResetMessage(msg);
    }
  };

  return (
    <>
      <Seo
        title="Entrar"
        description="Acesse sua conta Alpha Translations para acompanhar seus projetos de tradução juramentada, certificada e notarizada em Português, Espanhol e Inglês."
        path="/login"
      />
      {/* Hero Section - Fora do PageLayout */}
      <div className="w-full overflow-hidden" style={{ width: "100%", maxWidth: "100vw" }}>
        <Hero
          backgroundImage={fundoImage}
          title={t("home.heroTitle")}
          height="h-[300px] md:h-[400px]"
          overlay="bg-black/40"
        />
      </div>

      {/* Conteúdo Principal */}
      <div className="relative -mt-[225px] z-10 bg-transparent">
        <PageLayout transparent>
          <div className="font-poppins text-gray-800 min-h-screen flex flex-col bg-transparent">
            <main className="flex-grow">
              <FormContainer
                title={t("auth.loginTitle")}
                error={erro}
                footerText={t("auth.noAccount")}
                footerLinkText={t("common.register")}
                onFooterClick={() => navigate("/register")}
              >
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">
                      {t("auth.email")}:
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">
                      {t("auth.password")}:
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
                  >
                    {t("common.login")}
                  </button>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      {t("auth.forgotPassword")}
                    </button>
                  </div>
                </form>
              </FormContainer>
            </main>
          </div>
        </PageLayout>
      </div>

      {/* Modal de Recuperação de Senha */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {t("auth.resetPassword")}
            </h3>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("auth.resetPasswordMessage")}
                </label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  placeholder="seu@email.com"
                />
              </div>
              {resetMessage && (
                <div
                  className={`text-sm p-2 rounded ${
                    resetMessage.includes(
                      t("auth.resetEmailSent").split("!")[0]
                    )
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {resetMessage}
                </div>
              )}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
                >
                  {t("auth.sendResetLink")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail("");
                    setResetMessage("");
                  }}
                  className="flex-1 h-10 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rodapé */}
      <Footer />
    </>
  );
};

export default Login;
